import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useCountries} from '../hooks/useCountries';
import {Country} from '../types/geolocations';
import {hasAccess} from '../utils/user-access';

interface CountryContextType {
  selectedCountry: Country | null;
  setSelectedCountry: (country: Country | null) => void;
  countries: Country[];
  isLoading: boolean;
  error: unknown;
}

const CountryContext = createContext<CountryContextType | undefined>(undefined);

const STORAGE_KEY = 'selectedCountryId';

interface CountryProviderProps {
  children: ReactNode;
}

export function CountryProvider({children}: CountryProviderProps) {
  const {data: countries = [], isLoading, error} = useCountries();
  const [selectedCountry, setSelectedCountryState] = useState<Country | null>(
    null,
  );

  useEffect(() => {
    if (!countries.length) return;

    const savedCountryId = localStorage.getItem(STORAGE_KEY);
    if (savedCountryId) {
      const country = countries.find(c => c.id === Number(savedCountryId));
      if (country) {
        setSelectedCountryState(country);
        return;
      }
    }

    const defaultCountry = getDefaultCountry(countries);
    if (defaultCountry) {
      setSelectedCountryState(defaultCountry);
      localStorage.setItem(STORAGE_KEY, defaultCountry.id.toString());
    }
  }, [countries]);

  const getDefaultCountry = (availableCountries: Country[]): Country | null => {
    if (!availableCountries.length) return null;

    if (hasAccess('super-admin')) {
      const guatemala = availableCountries.find(c => c.id === 502);
      if (guatemala) return guatemala;
    }

    return availableCountries[0];
  };

  const setSelectedCountry = (country: Country | null) => {
    setSelectedCountryState(country);
    if (country) {
      localStorage.setItem(STORAGE_KEY, country.id.toString());
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const value = useMemo(
    () => ({
      selectedCountry,
      setSelectedCountry,
      countries,
      isLoading,
      error,
    }),
    [selectedCountry, countries, isLoading, error],
  );

  return (
    <CountryContext.Provider value={value}>{children}</CountryContext.Provider>
  );
}

export function useSelectedCountry() {
  const context = useContext(CountryContext);
  if (context === undefined) {
    throw new Error(
      'useSelectedCountry debe ser utilizado dentro de un CountryProvider',
    );
  }
  return context;
}
